import { FieldArray } from 'react-final-form-arrays'
import { Field, FieldRenderProps } from 'react-final-form'
import { cn } from 'cn'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid'
import QuantityInputGroupError from 'components/ui/QuantityInputGroupError'

function QuantitySelector({
  name,
  itemName,
  minValue,
  maxValue
}: {
  name: string
  itemName: string
  minValue: number
  maxValue: number
}) {
  return (
    <Field<number, FieldRenderProps<number, HTMLInputElement>, HTMLInputElement>
      name={name}
      render={({ input }) => {
        const prevDisabled = input.value === minValue
        const nextDisabled = input.value === maxValue

        const onPrev = () => {
          if (!prevDisabled && input.value > 0) input.onChange(input.value - 1)
        }
        const onNext = () => {
          if (!nextDisabled) input.onChange(input.value + 1)
        }

        return (
          <div className="flex items-center gap-2">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              aria-label={`Decrease quantity ${itemName}`}
              className={cn(
                'w-9 h-9 rounded-full border flex items-center justify-center outline-none-mouse transition-colors',
                {
                  'border-orange-300 cursor-pointer hover:bg-orange-50':
                    !prevDisabled,
                  'border-gray-200 cursor-auto': prevDisabled
                }
              )}
              onClick={onPrev}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onPrev()
              }}
              role="button"
              tabIndex={0}
            >
              <MinusIcon
                className={cn('w-5 h-5', {
                  'text-orange-500': !prevDisabled,
                  'text-orange-200': prevDisabled
                })}
              />
            </div>

            <p
              aria-label={`${input.value} ${
                input.value === 1 ? 'unit' : 'units'
              } of ${itemName}`}
              aria-live="assertive"
              className="text-lg font-semibold w-6 text-center"
            >
              {input.value}
            </p>

            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              aria-label={`Increase quantity ${itemName}`}
              className={cn(
                'w-9 h-9 rounded-full border flex items-center justify-center outline-none-mouse transition-colors',
                {
                  'border-orange-300 cursor-pointer hover:bg-orange-50':
                    !nextDisabled,
                  'border-gray-200 cursor-auto': nextDisabled
                }
              )}
              onClick={onNext}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onNext()
              }}
              role="button"
              tabIndex={0}
            >
              <PlusIcon
                className={cn('w-5 h-5', {
                  'text-orange-500': !nextDisabled,
                  'text-orange-200': nextDisabled
                })}
              />
            </div>
          </div>
        )
      }}
    />
  )
}

type Props = {
  name: string
}

export default function FormMealPartsField({ name }: Props) {
  return (
    <FieldArray name={name}>
      {({ fields, meta }) => {
        const error = meta.error || meta.submitError
        const showError = (meta.touched || meta.submitError) && !!error
        return (
          <div>
            <QuantityInputGroupError
              error={meta.error}
              submitError={meta.submitError}
              touched={meta.touched}
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {fields.map((part, id) => {
                const isActive = fields.value[id].count > 0
                return (
                  <div
                    key={part}
                    className={cn(
                      'rounded-xl border px-2 py-4 flex flex-col items-center gap-1 shadow-xs transition-colors',
                      {
                        'border-red-500': showError,
                        'border-orange-300 bg-orange-50':
                          !showError && isActive,
                        'bg-white border-gray-200': !showError && !isActive
                      }
                    )}
                  >
                    <p className="text-sm font-semibold text-gray-900">
                      {fields.value[id].name}
                    </p>
                    <div className="px-2">
                      <QuantitySelector
                        itemName={fields.value[id].name}
                        maxValue={9}
                        minValue={0}
                        name={`${part}.count`}
                      />
                    </div>
                    <p className="text-xs text-gray-500">per guest</p>
                  </div>
                )
              })}
            </div>
          </div>
        )
      }}
    </FieldArray>
  )
}
